<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  title: {
    type: String,
    default: '',
  },
  infoText: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  emptyText: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 border rounded-xl border-n-slate-3 bg-n-alpha-1"
  >
    <div
      v-if="title || $slots.actions"
      class="flex items-start justify-between gap-3"
    >
      <div class="flex flex-col gap-0.5 min-w-0">
        <h3
          v-if="title"
          class="flex items-center gap-1 text-sm font-medium text-n-slate-12"
        >
          <span class="truncate">{{ title }}</span>
          <fluent-icon
            v-if="infoText"
            v-tooltip="infoText"
            size="14"
            icon="info"
            class="text-n-slate-10 flex-shrink-0"
          />
        </h3>
      </div>
      <div v-if="$slots.actions" class="flex items-center gap-2 flex-shrink-0">
        <slot name="actions" />
      </div>
    </div>

    <div v-if="isLoading" class="flex flex-col gap-3 animate-pulse">
      <div class="h-4 w-1/3 rounded bg-n-slate-4" />
      <div class="h-24 w-full rounded bg-n-slate-4" />
    </div>

    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center gap-3 py-8 text-center"
    >
      <i class="i-lucide-alert-triangle w-6 h-6 text-n-amber-11" />
      <div class="flex flex-col gap-1">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.REPORTS.WIDGET.ERROR_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('KANBAN.REPORTS.WIDGET.ERROR_DESCRIPTION') }}
        </p>
      </div>
      <Button
        size="xs"
        variant="faded"
        color="slate"
        icon="i-lucide-rotate-cw"
        :label="t('KANBAN.REPORTS.WIDGET.RETRY')"
        @click="emit('retry')"
      />
    </div>

    <div
      v-else-if="isEmpty"
      class="flex items-center justify-center py-8 text-center"
    >
      <p class="text-sm text-n-slate-11">
        {{ emptyText || t('KANBAN.REPORTS.WIDGET.EMPTY') }}
      </p>
    </div>

    <slot v-else />
  </div>
</template>
